import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { GamePhase, CreateSessionData, TriviaAnswer } from '../types';
import { getSession } from '../services/api';

interface GameContextValue {
  sessionId: string | null;
  participantData: CreateSessionData | null;
  triviaAnswers: TriviaAnswer[];
  triviaScore: number;
  nickname: string;
  currentPhase: GamePhase;
  setSessionId: (id: string) => void;
  setParticipantData: (data: CreateSessionData) => void;
  addTriviaAnswer: (answer: TriviaAnswer) => void;
  setTriviaScore: (score: number) => void;
  setNickname: (nickname: string) => void;
  setPhase: (phase: GamePhase) => void;
}

const GameContext = createContext<GameContextValue | null>(null);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionId, setSessionIdState] = useState<string | null>(null);
  const [participantData, setParticipantData] = useState<CreateSessionData | null>(null);
  const [triviaAnswers, setTriviaAnswers] = useState<TriviaAnswer[]>([]);
  const [triviaScore, setTriviaScore] = useState(0);
  const [nickname, setNickname] = useState('');
  const [currentPhase, setCurrentPhase] = useState<GamePhase>('landing');

  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('session_id');
    if (storedSessionId) {
      setSessionIdState(storedSessionId);
      getSession(storedSessionId)
        .then((session) => {
          if (session.phase_completed >= 2) {
            setCurrentPhase('result');
          } else if (session.phase_completed >= 1) {
            setCurrentPhase('trivia');
          }
        })
        .catch(() => {
          sessionStorage.removeItem('session_id');
        });
    }
  }, []);

  const setSessionId = (id: string) => {
    setSessionIdState(id);
    sessionStorage.setItem('session_id', id);
    setCurrentPhase('trivia');
  };

  const addTriviaAnswer = (answer: TriviaAnswer) => {
    setTriviaAnswers((prev) => [...prev, answer]);
  };

  const setPhase = (phase: GamePhase) => {
    setCurrentPhase(phase);
  };

  return (
    <GameContext.Provider
      value={{
        sessionId,
        participantData,
        triviaAnswers,
        triviaScore,
        nickname,
        currentPhase,
        setSessionId,
        setParticipantData,
        addTriviaAnswer,
        setTriviaScore,
        setNickname,
        setPhase,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = (): GameContextValue => {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGame must be used within GameProvider');
  return context;
};
